import { Head, router } from "@inertiajs/react"
import { useEffect } from "react"
import { Building2, Calendar, ChevronDown, Download, DollarSign, FileText, FolderOpen, Layers, Loader2, Ruler } from "lucide-react"

import { FormattedDescription } from "@/components/formatted-description"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Separator } from "@/components/ui/separator"
import AppLayout from "@/layouts/app-layout"
import { brokerPropertiesPath } from "@/routes"
import type { BreadcrumbItem, Brochure, BrokerProfile, Property, Suite } from "@/types"

interface Props {
  property: Property
  suite: Suite
  broker_profile: BrokerProfile | null
  brochures: Brochure[]
}

const statusVariant: Record<Suite["status"], "default" | "secondary" | "destructive"> = {
  available: "default",
  under_negotiation: "secondary",
  leased: "destructive",
}

const statusLabel: Record<Suite["status"], string> = {
  available: "Available",
  under_negotiation: "Under Negotiation",
  leased: "Leased",
}

export default function Show({ property, suite, broker_profile, brochures }: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Properties", href: brokerPropertiesPath() },
    { title: property.name, href: `/broker/properties/${property.id}` },
    { title: `Suite ${suite.suite_number}`, href: `/broker/properties/${property.id}/suites/${suite.id}` },
  ]

  const handleGenerateBrochure = (style: "marketing" | "listing", branded: boolean) => {
    router.post(`/broker/properties/${property.id}/suites/${suite.id}/brochures`, {
      style,
      branded: branded ? "1" : "0",
    })
  }

  // Poll for status while any brochure is still generating.
  const pending = brochures.some((b) => b.status === "pending" || b.status === "processing")
  useEffect(() => {
    if (!pending) return
    const id = setInterval(() => {
      router.reload({ only: ["brochures"] })
    }, 3000)
    return () => clearInterval(id)
  }, [pending])

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Suite ${suite.suite_number} — ${property.name}`} />

      <div className="space-y-6 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold">Suite {suite.suite_number}</h1>
              <Badge variant={statusVariant[suite.status]}>{statusLabel[suite.status]}</Badge>
            </div>
            <p className="text-muted-foreground text-sm">{property.name} · {property.address}, {property.city}, {property.state}</p>
          </div>
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button variant="outline">
                    <FolderOpen className="mr-2 h-4 w-4" />
                    View Brochures
                    {brochures.length > 0 && (
                      <Badge variant="secondary" className="ml-2">{brochures.length}</Badge>
                    )}
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuGroup>
                  <DropdownMenuLabel>Brochures for this suite</DropdownMenuLabel>
                  {brochures.length === 0 && (
                    <div className="text-muted-foreground px-2 py-3 text-sm">
                      No brochures yet. Generate one to get started.
                    </div>
                  )}
                  {brochures.map((b) => (
                    <DropdownMenuItem
                      key={b.id}
                      disabled={!b.ready}
                      onClick={() => {
                        if (b.download_url) window.open(b.download_url, "_blank")
                      }}
                      className="flex items-start justify-between gap-2"
                    >
                      <div className="flex flex-col">
                        <span className="font-medium">{b.label}</span>
                        <span className="text-muted-foreground text-xs">
                          {new Date(b.created_at).toLocaleString()}
                        </span>
                      </div>
                      {b.status === "completed" ? (
                        <Download className="h-4 w-4 shrink-0" />
                      ) : b.status === "failed" ? (
                        <Badge variant="destructive">Failed</Badge>
                      ) : (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                      )}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>

            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button>
                    <FileText className="mr-2 h-4 w-4" />
                    Generate Brochure
                    <ChevronDown className="ml-2 h-4 w-4" />
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuGroup>
                  <DropdownMenuLabel>Marketing</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleGenerateBrochure("marketing", false)}>
                    Treystar-only · General
                  </DropdownMenuItem>
                  {broker_profile && (
                    <DropdownMenuItem onClick={() => handleGenerateBrochure("marketing", true)}>
                      Co-branded with your logo
                    </DropdownMenuItem>
                  )}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuLabel>Listing Detail Sheet</DropdownMenuLabel>
                  <DropdownMenuItem onClick={() => handleGenerateBrochure("listing", false)}>
                    Treystar-only · General
                  </DropdownMenuItem>
                  {broker_profile && (
                    <DropdownMenuItem onClick={() => handleGenerateBrochure("listing", true)}>
                      Co-branded with your logo
                    </DropdownMenuItem>
                  )}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Photos */}
        {suite.photo_urls && suite.photo_urls.length > 0 && (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {suite.photo_urls.map((url, i) => (
              <img
                key={i}
                src={url}
                alt={`Suite photo ${i + 1}`}
                loading="lazy"
                className="aspect-video w-full rounded-lg object-cover"
              />
            ))}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {suite.sqft && (
            <Card>
              <CardContent className="flex items-center gap-3 pt-4">
                <Ruler className="text-muted-foreground h-5 w-5" />
                <div>
                  <p className="text-muted-foreground text-xs">Square Footage</p>
                  <p className="font-medium">{Number(suite.sqft).toLocaleString()} sq ft</p>
                </div>
              </CardContent>
            </Card>
          )}
          {suite.monthly_rent && (
            <Card>
              <CardContent className="flex items-center gap-3 pt-4">
                <DollarSign className="text-muted-foreground h-5 w-5" />
                <div>
                  <p className="text-muted-foreground text-xs">Monthly Rent</p>
                  <p className="font-medium">${Number(suite.monthly_rent).toLocaleString()}/mo</p>
                </div>
              </CardContent>
            </Card>
          )}
          {suite.floor && (
            <Card>
              <CardContent className="flex items-center gap-3 pt-4">
                <Layers className="text-muted-foreground h-5 w-5" />
                <div>
                  <p className="text-muted-foreground text-xs">Floor</p>
                  <p className="font-medium">{suite.floor}</p>
                </div>
              </CardContent>
            </Card>
          )}
          {suite.available_on && (
            <Card>
              <CardContent className="flex items-center gap-3 pt-4">
                <Calendar className="text-muted-foreground h-5 w-5" />
                <div>
                  <p className="text-muted-foreground text-xs">Available On</p>
                  <p className="font-medium">{new Date(suite.available_on).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>
          )}
          <Card>
            <CardContent className="flex items-center gap-3 pt-4">
              <Building2 className="text-muted-foreground h-5 w-5" />
              <div>
                <p className="text-muted-foreground text-xs">Building</p>
                <p className="font-medium">{property.name}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {suite.description && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Description</CardTitle>
            </CardHeader>
            <CardContent>
              <FormattedDescription text={suite.description} />
            </CardContent>
          </Card>
        )}

        {broker_profile && (
          <>
            <Separator />
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Your Branding</CardTitle>
              </CardHeader>
              <CardContent className="flex items-center gap-4">
                {broker_profile.logo_url && (
                  <img src={broker_profile.logo_url} alt="Logo" className="h-10 w-auto object-contain" />
                )}
                <div>
                  {broker_profile.company_name && (
                    <p className="font-medium">{broker_profile.company_name}</p>
                  )}
                  {broker_profile.phone && (
                    <p className="text-muted-foreground text-sm">{broker_profile.phone}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AppLayout>
  )
}
